import { taskService, validateAndFixTask } from "../services/taskService";
import { topicService } from "../services/topicService";
import { posthogService } from "../services/posthogService";
import type { WorkSession } from "../types/task";

// Validate and fix session state after manual edits
function validateAndFixTaskSessions(task: any): any {
  // Use the exported validateAndFixTask function
  return validateAndFixTask(task);
}

export function useTaskOperations() {
  function updateTaskName(task: any, name: string) {
    const updatedTask = { ...task, name };
    return taskService.updateTask(updatedTask);
  }

  function updateTaskDuration(task: any, duration: number) {
    const updatedTask = { ...task, duration };
    return taskService.updateTask(updatedTask);
  }

  function updateTaskEstimate(task: any, estimatedDuration: number) {
    const updatedTask = { ...task, estimatedDuration };
    return taskService.updateTask(updatedTask);
  }

  function updateTaskTopic(task: any, topicId: string) {
    const topic = topicService.getTopicById(topicId);
    if (topic) {
      const updatedTask = { ...task, topicId };
      return taskService.updateTask(updatedTask);
    }
    return null;
  }

  function updateTaskProject(task: any, projectId?: string) {
    // Always explicitly set projectId - if undefined, set it to undefined to remove it
    // The taskService will handle removing undefined properties
    const updatedTask = { ...task, projectId };
    return taskService.updateTask(updatedTask);
  }

  function updateTaskSessions(task: any, sessions: WorkSession[]) {
    // Validate and fix session state
    const validatedTask = validateAndFixTaskSessions({ ...task, sessions });
    const updatedTask = { ...validatedTask, sessions };
    return taskService.updateTask(updatedTask);
  }

  function addTask(taskData: {
    name: string;
    topicId: string;
    projectId?: string;
    estimatedDuration: number;
  }) {
    const topic = topicService.getTopicById(taskData.topicId);
    const topicName = topic ? topic.name : "Other";
    const newTasks = taskService.addTask(
      taskData.name,
      topicName,
      taskData.estimatedDuration,
      taskData.projectId
    );

    // Track task creation in PostHog
    posthogService.trackTaskCreated(topicName);

    return newTasks;
  }

  function deleteTask(taskId: number, _tasks: any[]) {
    const newTasks = taskService.deleteTask(taskId);
    return newTasks;
  }

  function resetAllTasks() {
    return taskService.resetAllTasks();
  }

  function loadDefaultData() {
    // Reset topics to defaults first
    topicService.resetToDefaults();
    // Then load default tasks
    return taskService.loadDefaultData();
  }

  function saveTasks(tasks: any[]) {
    taskService.saveTasks(tasks);
  }

  function getTasks() {
    return taskService.getTasks();
  }

  return {
    updateTaskName,
    updateTaskDuration,
    updateTaskEstimate,
    updateTaskTopic,
    updateTaskProject,
    updateTaskSessions,
    addTask,
    deleteTask,
    resetAllTasks,
    loadDefaultData,
    saveTasks,
    getTasks,
  };
}
